import React, {
  ComponentType,
  SyntheticEvent,
  useCallback,
  useState
} from 'react'
import {
  Button,
  CheckboxProps,
  Form,
  Icon,
  InputOnChangeData,
  Message,
  Popup
} from 'semantic-ui-react'
// Local
import { API_URL, shouldRenderReskin2025 } from '../../constant'
import { api } from '../../action/api'
import If from '../common/if'
import ReveleerCoachingBox from '../ReveleerCoachingBox'

const SafeForm = Form as unknown as ComponentType<any>

/**
 * Provides an MFA form. Assumes that if the UI is showing the MFA code entry
 * form than the user has completed
 * @param id An ID attribute prefix used to identify component elements
 * @param onSubmit Login form submit callback function
 *        Arguments: MFA code, username, password, rememberMe
 * @param email Optional default email address
 * @param username Optional default username
 * @param password Optional default password
 * @param errorMessage Optional default error message
 */

type Props = {
  id: string
  onSubmit: (
    mfaCode: string,
    email: string,
    username: string,
    password: string,
    rememberMe: string
  ) => void
  email?: string
  username?: string
  password?: string
  errorMessage?: string | null
}

/** Mask email address for display */
const maskEmail = (email: string): string => {
  if (email && email.includes('@')) {
    const name = email.split('@')[0]
    const domain = email.split('@')[1]
    const first = name.slice(0, 1)
    const last = name.slice(-1)
    return `${first}...${last}@${domain}`
  }
  return email
}

/** Validate MFA code format */
const validateMfaCode = (code: string): boolean => {
  if (code) {
    return code.length === 7 && /^\d+$/.test(code)
  }
  return false
}

function MfaForm(props: Props): JSX.Element {
  const {
    id,
    onSubmit: onSubmitProp,
    email: emailProp,
    username: usernameProp,
    password: passwordProp,
    errorMessage
  } = props

  // Component state - email, username, password are initialized from props
  // and not modified (original class component behavior)
  const [ email ] = useState<string>(emailProp || '')
  const [ username ] = useState<string>(usernameProp || '')
  const [ password ] = useState<string>(passwordProp || '')
  const [
    mfaCode,
    setMfaCode
  ] = useState<string>('')
  const [
    shouldRememberMe,
    setShouldRememberMe
  ] = useState<boolean>(true)
  const [
    shouldShowResendStatus,
    setShouldShowResendStatus
  ] = useState<boolean>(false)
  const [
    isLoading,
    setIsLoading
  ] = useState<boolean>(false)

  const onSubmit = useCallback((): void => {
    if (
      username.length > 0 &&
      password.length > 0 &&
      validateMfaCode(mfaCode)
    ) {
      setIsLoading(true)
      onSubmitProp(
        mfaCode,
        email,
        username,
        password,
        shouldRememberMe ? 'true' : 'false'
      )
    }
  }, [
    email,
    username,
    password,
    mfaCode,
    shouldRememberMe,
    onSubmitProp
  ])

  const onClickResendStatusClose = useCallback((): void => {
    setShouldShowResendStatus(false)
  }, [])

  const resendMfaCode = useCallback((): void => {
    setIsLoading(true)
    api.postUnsafe(API_URL.MFA_CODE_RESEND, { email }).then(() => {
      setIsLoading(false)
      setShouldShowResendStatus(true)
    }).catch(() => {
      setIsLoading(false)
      // Error handled by api.defaultErrorHandler
      // User will see error message from default handler
    })
  }, [ email ])

  const onChangeMfaCode = useCallback((
    event: SyntheticEvent,
    data: InputOnChangeData
  ): void => {
    if (event) {
      setMfaCode(data.value || '')
    }
  }, [])

  const toggleRememberMe = useCallback((
    checked: boolean | undefined
  ): void => {
    setShouldRememberMe(checked || false)
  }, [])

  const rememberMePopupContent = 'By selecting this box, we will wait' +
    ' 30 days before we confirm your identity again. Do not select' +
    ' this option if this computer is used by multiple people.'

  return (
    <div
      id={ `mfa-${id}-login-container` }
      className="m-2-top"
    >
      <If isTrue={ !!errorMessage }>
        <If isTrue={ !shouldRenderReskin2025 }>
          <Message
            error={ true }
            content={ errorMessage }
          />
        </If>
        <If isTrue={ shouldRenderReskin2025 }>
          <ReveleerCoachingBox
            coachingBoxType="error"
            isShowing
            message={
              <p>
                { errorMessage }
              </p>
            }
          />
        </If>
      </If>
      <div className="mfa-login-form">
        <div className="m-1-top">
          <h2>Verify Your Identity</h2>
        </div>
        <div className="mfa-login-form-header m-1-top">
          For patient protection, additional verification is needed before
          you can continue.
        </div>
        <div className="m-1-top">
          <span>An email has been sent to</span>
          <span>&nbsp;</span>
          <strong>
            <span id={ `mfa-${id}-login-email-mask` }>
              { maskEmail(email) }
            </span>
          </strong>
          <span>&nbsp;</span>
          <span>with a verification code.</span>
        </div>
        <div className="mfa-login-form-warning">
          <strong>
            Do not close this screen. Open your email in a new screen to locate
            your verification code and enter the code below within 10 minutes.
          </strong>
        </div>
        <div className="m-1-top">
          <SafeForm
            onSubmit={ onSubmit }
            error={ errorMessage != null }
          >
            <Form.Group inline>
              <Form.Input
                id={ `mfa-${id}-login-code-input` }
                label="Verification Code:"
                placeholder="MFA Code"
                onChange={ onChangeMfaCode }
                name="mfaCode"
                value={ mfaCode }
              />
              <div className="m-1-top">
                <Button id={ `mfa-${id}-login-submit` }
                  color="blue" type="submit"
                  disabled={ !validateMfaCode(mfaCode) || isLoading }>
                  Verify
                </Button>
              </div>
            </Form.Group>
            <div>
              <Form.Group>
                <Form.Checkbox
                  id={ `mfa-${id}-login-remember-me` }
                  label="Remember Me"
                  onChange={
                    (event: SyntheticEvent, data: CheckboxProps) => {
                      if (event) {
                        toggleRememberMe(data.checked)
                      }
                    }
                  }
                  checked={ shouldRememberMe } />
                <Popup
                  content={ rememberMePopupContent }
                  mouseEnterDelay={ 500 }
                  trigger={
                    <Icon
                      name="info circle"
                      className="pointer"
                    />
                  }
                />
              </Form.Group>
            </div>
          </SafeForm>
          <div className="m-1-top">
            Not seeing this email? Check your spam/junk folder first for a
            message with the subject line: Your Curation Health Identity
            Verification Code.
            If you do not receive the email or your code expires, select Resend.
          </div>
          <div className="m-1-top text-align-center">
            <Button
              id={ `mfa-${id}-login-resend-button` }
              onClick={ resendMfaCode }
              disabled={ isLoading }
              className="mfa-login-form-resend"
              color="grey">Resend</Button>
          </div>
          <If isTrue={ shouldShowResendStatus }>
            <div
              id={ `mfa-${id}-login-resend-status` }
              className="m-1-top"
            >
              <If isTrue={ !shouldRenderReskin2025 }>
                <Message>
                  Resent verification code. Please check your email.
                  <span>&nbsp;</span>
                  <span
                    className="linkable"
                    onClick={ onClickResendStatusClose }
                  >
                    Ok
                  </span>
                </Message>
              </If>
              <If isTrue={ shouldRenderReskin2025 }>
                <ReveleerCoachingBox
                  coachingBoxType="infoPositive"
                  isShowing
                  message={
                    <>
                      Resent verification code. Please check your email.
                      <span>&nbsp;</span>
                      <span
                        className="linkable"
                        onClick={ onClickResendStatusClose }
                      >
                        Ok
                      </span>
                    </>
                  }
                />
              </If>
            </div>
          </If>
        </div>
      </div>
    </div>
  )
}

export default MfaForm
